/* eslint-disable parog-web/no-glint-expect-error-directive */
import { assert } from '@ember/debug';
import { registerDestructor } from '@ember/destroyable';
import { on } from '@ember/modifier';
import type Owner from '@ember/owner';
import { buildWaiter } from '@ember/test-waiters';
import Component from '@glimmer/component';
import formatDate from 'ember-intl/helpers/format-date';
import { modifier } from 'ember-modifier';
import or from 'ember-truth-helpers/helpers/or';
import { dateFormat } from 'parog-web/helpers/datepicker-format';
import fuiCalendar from 'wise-fui-modules/modifiers/fui-calendar';

export interface Signature {
  Element: HTMLDivElement;
  Args: {
    disabled?: unknown;
    selected?: Date;
    settings: {
      type: string;
    };
    format: string;
    isReadOnly?: boolean;
    onFocusOut: (value?: Date) => void;
  };
}

interface HTMLCalendarElement extends JQuery {
  calendar(command: string, value?: Date): void;
}

const waiter = buildWaiter('fui-calendar');

export default class FuiCalendarComponent extends Component<Signature> {
  get placeholder() {
    switch (this.args.settings.type) {
      case 'month':
        return 'ex. : 01/2007';
      case 'year':
        return 'ex. : 2007';
      case 'time':
        return 'ex. : 12:00';
      default:
        return 'ex. : 09/01/2017';
    }
  }

  get settings() {
    return Object.assign(
      {},
      {
        selectAdjacentDays: true,
        firstDayOfWeek: 1,
        monthFirst: false,
        text: {
          days: ['D', 'L', 'M', 'Me', 'J', 'V', 'S'],
          months: [
            'Janvier',
            'Février',
            'Mars',
            'Avril',
            'Mai',
            'Juin',
            'Juillet',
            'Août',
            'Septembre',
            'Octobre',
            'Novembre',
            'Décembre'
          ],
          monthsShort: [
            'Janv',
            'Févr',
            'Mars',
            'Avr',
            'Mai',
            'Juin',
            'Juil',
            'Août',
            'Sept',
            'Oct',
            'Nov',
            'Déc'
          ],
          today: "Aujourd'hui",
          now: 'Maintenant'
        },
        onChange: this.#onChange,
        onHide: this.focusOut,
        onHidden: this.#onHidden,
        onShow: this.#onShow,
        formatter: {
          date: dateFormat
        }
      },
      this.args.settings
    );
  }

  #currentValue = this.#selected;
  #isPopupOpen = false;
  // Fomantic hides the popup 70 ms after the blur, then plays a 200 ms closing
  // transition. Both are timers that `settled()` cannot see, so a test can act
  // on the page while the calendar is still drawn over it. The token is taken
  // as soon as a close becomes possible, and released once the popup is hidden.
  #waiterToken?: ReturnType<typeof waiter.beginAsync>;

  get #selected(): Date | undefined {
    if (
      typeof this.args.selected == 'string' &&
      !isNaN(Date.parse(this.args.selected))
    ) {
      return new Date(this.args.selected);
    }
    return this.args.selected;
  }

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    registerDestructor(this, this.#releaseWaiter);
  }

  focusOut = () => {
    if (this.args.isReadOnly) return;

    this.#holdUntilPopupIsHidden();
    this.args.onFocusOut?.(this.#currentValue);
  };

  setDateCalendar = modifier(calendar => {
    assert(
      'args.selected must be a date, a date string or undefined',
      this.#selected instanceof Date || this.#selected == undefined
    );

    if (this.#inputHasFocus(calendar)) return;

    this.#overwriteInputWithSelectedDate(calendar);
  });

  #holdUntilPopupIsHidden = (): void => {
    if (this.#isPopupOpen && this.#waiterToken === undefined) {
      this.#waiterToken = waiter.beginAsync();
    }
  };

  #inputHasFocus(calendar) {
    return calendar.contains(document.activeElement);
  }

  #onChange = (value: Date) => {
    this.#currentValue = value;
  };

  #onHidden = () => {
    this.#isPopupOpen = false;
    this.#releaseWaiter();
  };

  #onShow = () => {
    // Focusing the input again cancels a pending close: `onHidden` never fires,
    // so the token must be released here.
    this.#releaseWaiter();
    this.#isPopupOpen = this.args.isReadOnly !== true;

    return this.#isPopupOpen;
  };

  #overwriteInputWithSelectedDate(calendar) {
    // `calendar('set date')` also fires onChange: never call this while the
    // user is typing in the input, or their text will be erased.
    ($(calendar) as HTMLCalendarElement).calendar('set date', this.#selected);
  }

  #releaseWaiter = (): void => {
    if (this.#waiterToken !== undefined) {
      waiter.endAsync(this.#waiterToken);
      this.#waiterToken = undefined;
    }
  };

  <template>
    {{! prettier-ignore }}
    <div
      data-test-fui-calendar
      {{! @glint-expect-error }}
      class='ui {{if (or @isReadOnly @disabled) "disabled"}} calendar'
      ...attributes
      {{fuiCalendar this.settings}}
      {{this.setDateCalendar}}
    >
      {{#if (has-block)}}

              {{! @glint-expect-error }}
        {{yield (formatDate @selected year='numeric' month='numeric' day='numeric') }}
      {{else}}
        <input
              {{! @glint-expect-error }}
          id={{@id}}
          type='text'
          placeholder={{this.placeholder}}
              {{! @glint-expect-error }}
          disabled={{@disabled}}
          readonly={{@isReadOnly}}
          {{on 'focusout' this.focusOut}}
        />
      {{/if}}
    </div>
  </template>
}
